import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, useAuth } from "@/context/AuthContext";
import { Calendar, Receipt, FileText, MessageSquare, ArrowUpRight, Sparkles, Loader2 } from "lucide-react";

function Stat({ icon: Icon, label, value, to }) {
  return (
    <Link to={to} className="card p-5 block hover:-translate-y-1 transition" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-navy/55">{label}</div>
          <div className="font-serif text-3xl text-navy mt-1">{value}</div>
        </div>
        <Icon className="w-7 h-7 text-gold" />
      </div>
    </Link>
  );
}

export default function PortalDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/portal/dashboard")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="flex items-center gap-2 text-navy/60 p-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );

  const d = data || {};
  return (
    <div className="space-y-8" data-testid="portal-dashboard">
      <header>
        <div className="eyebrow"><Sparkles className="inline w-3 h-3 -mt-0.5 mr-1" /> Your hub</div>
        <h1 className="mt-2 text-4xl">Welcome back, {user?.name?.split(" ")[0]}.</h1>
        <p className="mt-2 text-navy/65">A quick view of your services and what needs your attention.</p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Calendar} label="Upcoming Appts" value={d.upcoming_appointments?.length || 0} to="/portal/appointments" />
        <Stat icon={Receipt} label="Open Invoices" value={d.outstanding_invoices?.length || 0} to="/portal/invoices" />
        <Stat icon={FileText} label="Documents" value={d.recent_documents?.length || 0} to="/portal/documents" />
        <Stat icon={MessageSquare} label="Messages" value={d.recent_messages?.length || 0} to="/portal/messages" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card p-7">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-2xl">Active Services</h3>
            <Link to="/portal/services" className="text-sm text-gold hover:underline">View all →</Link>
          </div>
          {(d.active_services || []).length === 0 ? (
            <div className="text-navy/55 text-sm">
              No active services yet. <Link to="/services" className="text-gold underline">Browse services</Link> to get started.
            </div>
          ) : (
            <ul className="space-y-3">
              {d.active_services.slice(0, 5).map((s) => (
                <li key={s.id || s.service_id || s.name} className="flex items-center justify-between border-b border-navy/5 last:border-0 pb-3">
                  <span className="text-navy">{s.name || s.service_name}</span>
                  <span className="text-xs uppercase tracking-widest text-teal">{s.status || "active"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card p-7">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-2xl">Upcoming Appointments</h3>
            <Link to="/portal/appointments" className="text-sm text-gold hover:underline">View all →</Link>
          </div>
          {(d.upcoming_appointments || []).length === 0 ? (
            <div className="text-navy/55 text-sm">
              Nothing scheduled. <Link to="/book" className="text-gold underline">Book a consultation</Link>.
            </div>
          ) : (
            <ul className="space-y-3">
              {d.upcoming_appointments.map((a) => (
                <li key={a.appointment_id} className="flex items-center justify-between border-b border-navy/5 last:border-0 pb-3">
                  <div>
                    <div className="text-navy">{a.title}</div>
                    <div className="text-xs text-navy/55">{a.starts_at}</div>
                  </div>
                  <span className="text-xs uppercase tracking-widest text-gold">{a.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="card p-7 bg-navy text-soft">
        <h3 className="text-2xl text-soft">Need something else?</h3>
        <p className="mt-2 text-soft/70 max-w-xl">Add a service, reschedule, or send us a message — we're here.</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/book" data-testid="portal-cta-book" className="btn-primary">Book Appointment <ArrowUpRight className="w-4 h-4" /></Link>
          <Link to="/portal/messages" data-testid="portal-cta-message" className="inline-flex items-center justify-center gap-2 bg-transparent border border-soft text-soft hover:bg-soft hover:text-navy rounded-full px-7 py-3 text-sm uppercase tracking-wider transition">Message Team</Link>
        </div>
      </div>
    </div>
  );
}
